import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useState } from "react";
import { auth, db } from "../config/firebase";

export function MessageInput({ 
  spotifyProfile, 
  currentlyPlaying, 
  isPlayingFromZero,
  isPremium, 
  isLoading, 
  replyingTo, 
  onCancelReply
}) {
    const [message, setMessage] = useState('');
    const messagesRef = collection(db, 'messages');

    const getInputPlaceholder = () => {
      if (isLoading) return "Loading Spotify status...";
      if (!spotifyProfile) return "Connect Spotify to chat";
      if (!isPremium) return "Spotify Premium required to chat";
      if (!currentlyPlaying?.is_playing) return "Play music to chat";
      if (!isPlayingFromZero) return "Play a From Zero track to chat";
      if (replyingTo) return `Reply to ${replyingTo.userName}...`;
      return "Type a message...";
    };

    const isInputDisabled = !spotifyProfile || 
                          isLoading || 
                          !isPremium || 
                          !currentlyPlaying?.is_playing || 
                          !isPlayingFromZero;

    const sendMessage = async (e) => {
      e.preventDefault();
      if (!message.trim() || isInputDisabled) return;
  
      const messageData = {
        text: message,
        createdAt: serverTimestamp(),
        userId: auth.currentUser.uid,
        userName: spotifyProfile.display_name,
        userPhoto: spotifyProfile.images?.[0]?.url,
        currentTrack: currentlyPlaying?.item ? {
          name: currentlyPlaying.item.name,
          artist: currentlyPlaying.item.artists[0].name,
          albumArt: currentlyPlaying.item.album.images[0]?.url,
        } : null
      };
  
      if (replyingTo && replyingTo.id && replyingTo.userName && replyingTo.text) {
        messageData.replyTo = {
          id: replyingTo.id,
          userName: replyingTo.userName,
          text: replyingTo.text
        };
      }
  
      try {
        await addDoc(messagesRef, messageData);
        // console.log('Added document with ID:', docRef.id);
        setMessage('');
        if (onCancelReply) {
          onCancelReply();
        }
      } catch (error) {
        console.error("Error sending message:", error);
      }
    };

    const getAlertMessage = () => {
      if (!isPremium) {
        return {
          message: "You need Spotify Premium to participate in the chat.",
          type: "premium"
        };
      }
      if (!currentlyPlaying?.is_playing) {
        return {
          message: "Start playing music on Spotify to join the chat",
          type: "warning"
        };
      }
      if (!isPlayingFromZero) {
        return {
          message: "You need to be playing a From Zero track to participate in the chat",
          type: "warning"
        };
      }
      return null;
    };

    const alertInfo = getAlertMessage();
  
    return (
      <div className="space-y-4">
        {!isPremium && (
          <div className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 px-5 py-4 rounded-xl">
            <div className="flex items-center gap-3">
              <span className="text-2xl">💿</span>
              <span className="text-sm font-medium text-gray-800">
                Spotify Premium required to chat
              </span>
            </div>
            <a 
              href="https://www.spotify.com/premium" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors"
            >
              Upgrade to Premium
              <span className="ml-1">→</span>
            </a>
          </div>
        )}
        
        {alertInfo?.type === "warning" && (
          <div className="bg-amber-50 border border-amber-200 px-5 py-4 rounded-xl">
            <span className="text-sm font-medium text-amber-800">
              {alertInfo.message}
            </span>
          </div>
        )}

        {replyingTo && (
          <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
            <div className="text-sm text-gray-600">
              Replying to <span className="font-medium">{replyingTo.userName}</span>
            </div>
            <button 
              onClick={onCancelReply}
              className="text-gray-500 hover:text-red-500"
            >
              ✕
            </button>
          </div>
        )}

        <form onSubmit={sendMessage} className="flex gap-3">
          <input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isInputDisabled}
            className="flex-1 px-5 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
            placeholder={getInputPlaceholder()}
          />
          <button
            type="submit"
            disabled={isInputDisabled}
            className="px-6 py-3 bg-blue-500 text-white font-medium rounded-xl hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Send
          </button>
        </form>
      </div>
    );
}